import { Component, EventEmitter, Input, OnChanges, Output, TemplateRef } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Select, Store } from '@ngxs/store';
import { CheckoutPaymentInformationState, ProductState } from '@platform-frontend/checkout';
import {
  isReusablePaymentMethod,
  MARKETPLACE_MATOMO_EVENT_CATEGORY_PAYMENT,
  PaymentMethod,
  PaymentMethodBackendIdentifier,
  PaymentMethodIdentifier,
  PaymentMethodInfo,
  PaymentMethodType,
  WalletPaymentMethodType
} from '@platform-frontend/marketplace';
import { ClearNotifications, Navigate, TypedChanges } from '@platform-frontend/util';
import { BehaviorSubject, Observable } from 'rxjs';
import { NavigationService } from '../../navigation.service';
import { PaymentIdWithCallback } from '../payment-id-with-callback';

@UntilDestroy()
@Component({
  selector: 'yat-payment-method-selection-common',
  templateUrl: './payment-method-selection-common.component.html',
  styleUrls: ['./payment-method-selection-common.component.scss']
})
export class PaymentMethodSelectionCommonComponent implements OnChanges {
  constructor(private activatedRoute: ActivatedRoute, private store: Store, private navigationService: NavigationService) {}

  private _samePaymentMethodSelected$ = new BehaviorSubject<boolean>(false);

  @Input()
  selectedPaymentMethod: PaymentMethod | PaymentMethodInfo | null = null;

  @Input()
  updateSubscriptionWithContractId: string | null = null;

  @Input()
  allowToDeleteUsed: boolean = true;

  @Input()
  paymentMethods: (PaymentMethod | PaymentMethodInfo)[] = [];

  @Input()
  showWalletButtons: boolean = false;

  @Input()
  walletButtonsTemplate?: TemplateRef<any>;

  @Output()
  saveSelection: EventEmitter<PaymentIdWithCallback> = new EventEmitter();

  @Output()
  prepareDeleteMethod: EventEmitter<PaymentMethodIdentifier> = new EventEmitter();

  @Output()
  deleteMethod: EventEmitter<PaymentIdWithCallback> = new EventEmitter();

  @Select(ProductState.isLive)
  isLive$: Observable<boolean>;

  @Select(CheckoutPaymentInformationState.canPaymentMethodsBeCreated)
  canPaymentMethodsBeCreated$: Observable<boolean>;

  @Select(ProductState.allowedPaymentMethods)
  allowedPaymentMethods$: Observable<PaymentMethodType[]>;

  selectedId: number | WalletPaymentMethodType | null;
  stashedWallet: WalletPaymentMethodType | null = null;

  readonly MATOMO_EVENT_CATEGORY_PAYMENT = MARKETPLACE_MATOMO_EVENT_CATEGORY_PAYMENT;

  ngOnChanges(changes: TypedChanges<PaymentMethodSelectionCommonComponent>): void {
    if (changes.selectedPaymentMethod) {
      if (this.selectedPaymentMethod === null) {
        this.selectedId = null;
        this.stashedWallet = null;
      } else {
        this.selectedId = isReusablePaymentMethod(this.selectedPaymentMethod) ? this.selectedPaymentMethod.id : this.selectedPaymentMethod.type;
        this.stashedWallet = this.getWalletButtonToEnable(this.selectedPaymentMethod);
      }
    }
  }

  get samePaymentMethodSelected$(): Observable<boolean> {
    return this._samePaymentMethodSelected$.asObservable();
  }

  trackPaymentMethod(_: number, paymentMethod: PaymentMethod | PaymentMethodInfo): number | WalletPaymentMethodType {
    return isReusablePaymentMethod(paymentMethod) ? paymentMethod.id : paymentMethod.type;
  }

  select(paymentMethod: PaymentMethod | PaymentMethodInfo, completeCallback: () => void): void {
    if (!this.isSelected(paymentMethod)) {
      this._samePaymentMethodSelected$.next(false);
      const walletButtonToEnable = this.getWalletButtonToEnable(paymentMethod);
      if (walletButtonToEnable !== null) {
        this.store.dispatch(new ClearNotifications());
        this.stashAndComplete(walletButtonToEnable, completeCallback);
      } else {
        if (
          this.showWalletButtons &&
          isReusablePaymentMethod(paymentMethod) &&
          this.selectedPaymentMethod &&
          isReusablePaymentMethod(this.selectedPaymentMethod) &&
          this.selectedPaymentMethod.id === paymentMethod.id
        ) {
          this.stashAndComplete(null, completeCallback);
        } else {
          this.saveSelection.emit({
            identifier: this.mapToPaymentMethodBackendIdentifier(paymentMethod),
            completeCallback: () => this.stashAndComplete(null, completeCallback)
          });
        }
      }
    } else {
      this._samePaymentMethodSelected$.next(true);
      completeCallback();
    }
  }

  private getWalletButtonToEnable(paymentMethod: PaymentMethod | PaymentMethodInfo): WalletPaymentMethodType | null {
    return this.showWalletButtons &&
      !isReusablePaymentMethod(paymentMethod) &&
      this.store.selectSnapshot(ProductState.isWalletPaymentMethodAllowed)(paymentMethod.type)
      ? paymentMethod.type
      : null;
  }

  isUsed(paymentMethod: PaymentMethod | PaymentMethodInfo): boolean {
    return isReusablePaymentMethod(paymentMethod) ? this.selectedId === paymentMethod.id : this.selectedId === paymentMethod.type;
  }

  isSelected(paymentMethod: PaymentMethod | PaymentMethodInfo): boolean {
    if (this.stashedWallet !== null) {
      return this.stashedWallet === paymentMethod.type;
    }
    return this.isUsed(paymentMethod);
  }

  editSelectedPaymentMethod(paymentMethod: PaymentMethod | PaymentMethodInfo): void {
    if (isReusablePaymentMethod(paymentMethod)) {
      const queryParamsForEdit = {
        id: paymentMethod.id
      };
      this.store.dispatch(new Navigate(['../payment-method-with-data'], { relativeTo: this.activatedRoute, queryParams: queryParamsForEdit }));
    }
  }

  prepareDelete(paymentMethod: PaymentMethod | PaymentMethodInfo): void {
    setTimeout(() => this.prepareDeleteMethod.next(this.mapToPaymentMethodIdentifier(paymentMethod)));
  }

  cancelDelete(): void {
    this.store.dispatch(new ClearNotifications());
  }

  delete(paymentMethod: PaymentMethod | PaymentMethodInfo, completeCallback: () => void): void {
    this.deleteMethod.emit({
      identifier: this.mapToPaymentMethodBackendIdentifier(paymentMethod),
      completeCallback: () => this.stashAndComplete(null, completeCallback)
    });
  }

  private stashAndComplete(stashedWallet: WalletPaymentMethodType | null, completeCallback: () => void): void {
    this.stashedWallet = stashedWallet;
    completeCallback();
  }

  modalClosed(): void {
    this._samePaymentMethodSelected$.next(false);
  }

  continue(): void {
    this.navigationService.gotoConfirmationOrOverview(this.activatedRoute).pipe(untilDestroyed(this)).subscribe();
  }

  private mapToPaymentMethodIdentifier(paymentMethod: PaymentMethod | PaymentMethodInfo): PaymentMethodIdentifier {
    return isReusablePaymentMethod(paymentMethod) ? { id: paymentMethod.id } : { type: paymentMethod.type };
  }

  private mapToPaymentMethodBackendIdentifier(paymentMethod: PaymentMethod | PaymentMethodInfo): PaymentMethodBackendIdentifier {
    return isReusablePaymentMethod(paymentMethod)
      ? { id: paymentMethod.id, serversideVersion: paymentMethod.serversideVersion }
      : { type: paymentMethod.type, serversideVersion: paymentMethod.serversideVersion };
  }

  isWalletButtonAllowed(walletType: WalletPaymentMethodType | null, allowedPaymentMethods: PaymentMethodType[]): boolean {
    return walletType !== null && allowedPaymentMethods.includes(walletType);
  }
}
